
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyPerksStore } from '@/lib/stores/companyPerksStore';
import { useToast } from '@/hooks/use-toast';

export interface PerksFormData {
  perk1: string;
  perk2: string;
  perk3: string;
  perk4: string;
  perk5: string;
}

export const usePerksForm = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { perks, isLoading: storeLoading, error: storeError, fetchPerks, savePerks } = useCompanyPerksStore();
  
  const form = useForm<PerksFormData>({
    defaultValues: {
      perk1: '',
      perk2: '',
      perk3: '',
      perk4: '',
      perk5: '',
    },
  });

  useEffect(() => {
    const loadPerks = async () => {
      if (!user?.user_metadata?.company_id) {
        console.log('[PerksForm] No company ID found in user metadata');
        return;
      }

      const companyId = user.user_metadata.company_id;
      console.log('[PerksForm] Loading perks for company ID:', companyId);
      
      try {
        const fetchedPerks = await fetchPerks(companyId);
        
        if (fetchedPerks) {
          console.log('[PerksForm] Setting form values with fetched perks');
          form.reset({
            perk1: fetchedPerks.perk1 || '',
            perk2: fetchedPerks.perk2 || '',
            perk3: fetchedPerks.perk3 || '',
            perk4: fetchedPerks.perk4 || '',
            perk5: fetchedPerks.perk5 || '',
          });
        } else {
          console.log('[PerksForm] No perks found, using default empty values');
        }
      } catch (error) {
        console.error('[PerksForm] Error in loadPerks:', error);
      }
    };
    
    loadPerks();
  }, [user, fetchPerks, form]);
  
  // Fix: Moved useEffect to avoid dependency on storeError which might cause too many re-renders
  useEffect(() => {
    if (storeError) {
      console.error('[PerksForm] Store error detected:', storeError);
      
      // Don't toast on initial render if there's an error
      if (typeof storeError === 'string' && storeError.trim() !== '') {
        toast({
          title: "Error",
          description: storeError || "An error occurred",
          variant: "destructive"
        });
      }
    }
  }, [storeError, toast]);

  const handleSubmit = async (data: PerksFormData) => {
    if (!user?.user_metadata?.company_id) {
      console.error('[PerksForm] No company ID found in user metadata');
      toast({
        title: "Error",
        description: "Company ID not found. Please contact support.",
        variant: "destructive"
      });
      return;
    }

    const companyId = user.user_metadata.company_id;
    console.log('[PerksForm] Submitting perks for company ID:', companyId);
    console.log('[PerksForm] Form data:', data);
    
    setLoading(true);
    
    try {
      const success = await savePerks({
        companyId,
        perk1: data.perk1 || undefined,
        perk2: data.perk2 || undefined,
        perk3: data.perk3 || undefined,
        perk4: data.perk4 || undefined,
        perk5: data.perk5 || undefined,
      });
      
      if (success) {
        toast({
          title: "Success",
          description: "Company perks have been saved successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to save company perks. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('[PerksForm] Error in handleSubmit:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    isLoading: loading || storeLoading,
    perks,
    onSubmit: handleSubmit
  };
};
